
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { getUserFamilyId } from './helpers';
import { Memory } from '@/types/memory';
import { logger } from '@/utils/logger';

// Quotes storage functions
export interface QuoteItem {
  id: string;
  content: string;
  childId: string;
  childName: string;
  date: string;
  context?: string;
}

export const getStoredQuotes = async (): Promise<QuoteItem[]> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      return [];
    }
    
    const familyId = await getUserFamilyId();
    if (!familyId) return [];
    
    // Need to use two separate queries since join syntax errors
    const { data: quotes, error: quotesError } = await supabase
      .from('quotes')
      .select('*')
      .eq('family_id', familyId)
      .order('date', { ascending: false });

    if (quotesError) {
      logger.error('Error fetching quotes:', quotesError);
      return [];
    }

    // Early return if no quotes
    if (!quotes || quotes.length === 0) return [];

    // Now get child names for all quotes
    const childIds = [...new Set(quotes.map(quote => quote.child_id))];
    const { data: children, error: childrenError } = await supabase
      .from('children')
      .select('id, name')
      .in('id', childIds);

    if (childrenError) {
      logger.error('Error fetching children for quotes:', childrenError);
      return [];
    }

    if (!children) return [];

    // Create a map of child id to name for quick lookup
    const childMap = new Map();
    children.forEach(child => {
      childMap.set(child.id, child.name);
    });

    return quotes.map(quote => ({
      id: quote.id,
      content: quote.content,
      childId: quote.child_id,
      childName: childMap.get(quote.child_id) || 'Unknown Child',
      date: quote.date,
      context: quote.context
    }));
  } catch (error) {
    logger.error('Error in getStoredQuotes:', error);
    return [];
  }
};

export const storeQuotes = async (quote: Omit<QuoteItem, 'id' | 'childName'>): Promise<QuoteItem | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      logger.error('No authenticated user found');
      return null;
    }
    
    const familyId = await getUserFamilyId();
    if (!familyId) {
      logger.error('User has no family');
      return null;
    }
    
    // First get the child's name
    const { data: childData, error: childError } = await supabase
      .from('children')
      .select('name')
      .eq('id', quote.childId)
      .single();

    if (childError) {
      logger.error('Error fetching child name:', childError);
      return null;
    }

    const { data, error } = await supabase
      .from('quotes')
      .insert({
        content: quote.content,
        child_id: quote.childId,
        date: quote.date,
        context: quote.context,
        user_id: userData.user.id,
        family_id: familyId
      })
      .select()
      .single();

    if (error) {
      logger.error('Error storing quote:', error);
      return null;
    }

    if (!data || !childData) return null;

    return {
      id: data.id,
      content: data.content,
      childId: data.child_id,
      childName: childData.name,
      date: data.date,
      context: data.context
    };
  } catch (error) {
    logger.error('Error in storeQuotes:', error);
    return null;
  }
};

// Update quote function
export const updateQuote = async (quote: QuoteItem): Promise<QuoteItem | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      logger.error('No authenticated user found');
      return null;
    }
    
    const { data, error } = await supabase
      .from('quotes')
      .update({
        content: quote.content,
        child_id: quote.childId,
        date: quote.date,
        context: quote.context
      })
      .eq('id', quote.id)
      .eq('user_id', userData.user.id)
      .select()
      .single();

    if (error) {
      logger.error('Error updating quote:', error);
      return null;
    }

    if (!data) return null;

    return {
      ...quote,
      id: data.id,
      content: data.content,
      childId: data.child_id,
      date: data.date,
      context: data.context
    };
  } catch (error) {
    logger.error('Error in updateQuote:', error);
    return null;
  }
};

// Delete quote function
export const deleteQuote = async (quoteId: string): Promise<boolean> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      logger.error('No authenticated user found');
      return false;
    }

    const { error } = await supabase
      .from('quotes')
      .delete()
      .eq('id', quoteId)
      .eq('user_id', userData.user.id);

    if (error) {
      logger.error('Error deleting quote:', error);
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error in deleteQuote:', error);
    return false;
  }
};

// Convert to Memory type
export const quoteToMemory = (quote: QuoteItem): Memory => ({
  id: quote.id,
  content: quote.content,
  childName: quote.childName,
  childId: quote.childId,
  date: quote.date,
  type: 'quote',
  context: quote.context
});
